import React, { useEffect, useMemo, useState } from 'react'
import { Form, Container, Row, Col } from 'react-bootstrap'
import {
  CompanyInfoModal,
  Footer,
  KonektoNavbar,
  Loader,
  MessageModal,
  PaginationList,
  Product,
  ProductDetails,
  Slider,
} from '../../components'
import './CompanyInfo.scss'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { companyService } from '../../services/companyService'
import { MESSAGE_TYPE } from '../../constants/config'
import { BsTelephone } from 'react-icons/bs'
import { IoMailOutline, IoPeopleOutline } from 'react-icons/io5'
import { RxExternalLink } from 'react-icons/rx'
import { MdWorkspacesOutline } from 'react-icons/md'
import { LuClock } from 'react-icons/lu'
import { GoVerified } from 'react-icons/go'
import { hasValue } from '../../helpers'
import { IoMdClose, IoMdSearch } from 'react-icons/io'
import { productService } from '../../services/productService'
import { FiMapPin } from 'react-icons/fi'
import { MdOutlineInfo } from 'react-icons/md'
import { LuMessageCircleMore } from 'react-icons/lu'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { saveVisitedCompany } from '../../store/Actions/Company'

export default function CompanyInfo() {
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const { token } = useSelector(state => state.authentication)

  const queryParams = new URLSearchParams(location.search)
  const currentPage = queryParams.get('page') || 1
  const qParam = queryParams.get('q', '')
  const productIdParam = queryParams.get('p') || undefined

  const { companyId } = useParams()

  const myCompany = useSelector(state => state.company?.myCompany)
  const visitedCompanies = useSelector(
    state => state.company?.visitedCompanies ?? [],
  )

  const [company, setCompany] = useState()
  const [companyProducts, setCompanyProducts] = useState([])
  const [loader, setLoader] = useState(false)

  const [totalPages, setTotalPages] = useState(1)

  const [searchQuery, setSearchQuery] = useState('')

  const [selectedProduct, setSelectedProduct] = useState()
  const [productLoader, setProductLoader] = useState(false)

  const [isInfoModalVisible, setIsInfoModalVisible] = useState(false)

  const [isLargeImagesSliderVisible, setIsLargeImagesSliderVisible] =
    useState(false)

  const [messagesModalInfo, setMessagesModalInfo] = useState({
    visible: false,
    type: null,
  })

  const isMyCompany = useMemo(() => {
    return companyId == myCompany?.id
  }, [companyId, myCompany])

  const companyStatus = useMemo(() => {
    const isVerified = company?.is_verified

    if (isVerified === null || isVerified === 0) {
      return (
        <div>
          <div className="mb-3 d-flex align-items-center">
            <LuClock className="fs-5 me-1" color="orange" />
            <span>Nije verifikovano</span>
          </div>
        </div>
      )
    } else if (isVerified === 1) {
      return (
        <div>
          <div className="mb-3 d-flex align-items-center">
            <GoVerified className="fs-5 me-1" color="green" />
            <span>Verifikovano</span>
          </div>
        </div>
      )
    } else {
      return null
    }
  }, [company])

  const companyAddress = useMemo(() => {
    let fullAddress = ''

    if (!company) {
      return fullAddress
    }

    if (company.address) {
      fullAddress = `${company.address}`
    }
    if (company.zip_code) {
      fullAddress = `${fullAddress}, ${company.zip_code}`
    }
    if (company.city) {
      fullAddress = `${fullAddress}, ${company.city}`
    }

    return fullAddress
  }, [company])

  const getCompanyProducts = async ({ companyId, page, search }) => {
    const response = await companyService.getCompanyProducts({
      companyId,
      page,
      search,
    })

    if (response.isOk && response.data) {
      setTotalPages(response.data.last_page)
      setCompanyProducts(response.data.data)
    }
  }

  const onSearchSubmit = e => {
    e.preventDefault()

    if (!hasValue(searchQuery) && !hasValue(qParam)) {
      return
    }

    queryParams.set('page', 1)

    if (hasValue(searchQuery)) {
      queryParams.set('q', searchQuery)
    } else {
      queryParams.delete('q')
    }

    navigate(`?${queryParams.toString()}`)
    window.scrollTo({ top: 100, behavior: 'smooth' })
  }

  const clearSearch = () => {
    setSearchQuery('')

    if (hasValue(qParam)) {
      queryParams.delete('q')
    }

    queryParams.set('page', 1)
    navigate(`?${queryParams.toString()}`)
    window.scrollTo({ top: 100, behavior: 'smooth' })
  }

  const handlePageChange = newPage => {
    queryParams.set('page', newPage)
    navigate(`?${queryParams.toString()}`)
    window.scrollTo({ top: 100, behavior: 'smooth' })
  }

  const onCardClick = async productId => {
    queryParams.set('p', productId)
    navigate(`?${queryParams.toString()}`)
    window.scrollTo({ top: 50, behavior: 'smooth' })
  }

  const onPrivateMessageClick = () => {
    if (!token) {
      toast.info('Prijavite se da pošaljete poruku.')
      return
    }

    setMessagesModalInfo({ visible: true, type: MESSAGE_TYPE.private })
  }

  const openProductMessageModal = () => {
    if (!token) {
      toast.info('Prijavite se da pošaljete poruku.')
      return
    }

    setMessagesModalInfo({ visible: true, type: MESSAGE_TYPE.product })
  }

  const onSlideImageClick = imageIndex => {
    setIsLargeImagesSliderVisible(true)
  }

  const slides = useMemo(() => {
    const images = selectedProduct?.images?.map(image => {
      return image.path
    })

    return images ?? []
  }, [selectedProduct])

  useEffect(() => {
    if (!companyId) {
      return
    }

    const getCompany = async () => {
      setLoader(true)

      const visitedCompany = visitedCompanies?.find(
        c => c.companyId === companyId,
      )
      const now = Date.now()
      let shouldCount = true

      if (visitedCompany) {
        const diffInMinutes = (now - visitedCompany.visitTime) / (1000 * 60)
        shouldCount = diffInMinutes > 60
      }

      if (shouldCount) {
        dispatch(saveVisitedCompany(companyId))
      }

      const response = await companyService.getCompanyInfo(
        companyId,
        shouldCount,
      )

      if (response.isOk) {
        setCompany(response.data)
      }

      setLoader(false)
    }

    getCompany()
  }, [companyId])

  useEffect(() => {
    if (companyId && currentPage) {
      getCompanyProducts({ companyId, page: currentPage, search: qParam })
    }
  }, [companyId, currentPage, qParam, token])

  useEffect(() => {
    const getProduct = async () => {
      setProductLoader(true)

      const response = await productService.getProduct({ id: productIdParam })
      if (response.isOk && response.data) {
        setSelectedProduct(
          response.data.company_id == companyId ? response.data : undefined,
        )
      }

      setProductLoader(false)
    }

    if (productIdParam) {
      getProduct(productIdParam)
    }
  }, [productIdParam, companyId])

  return (
    <div id="company-info">
      <KonektoNavbar />
      <Container className="my-5 w-100">
        {loader ? <Loader /> : null}

        {company ? (
          <div className="d-flex flex-column flex-sm-row">
            {/** Company info */}
            <div className="group-wrapper shadow company-info">
              <div>
                {companyStatus}
                <img src={company.cover_image} className="company-logo" />
              </div>
              <h3 className="mt-3">{company.name}</h3>
              <h6>
                <b>JIB:</b>
                {` ${company.jib}`}
              </h6>
              <div className="d-flex align-items-center">
                <FiMapPin className="me-2" />
                <h6 className="mb-0">{companyAddress}</h6>
              </div>

              <hr className="hr divider" />

              {company?.phone ? (
                <div
                  className="d-flex align-items-center pointer item-link"
                  onClick={() => {
                    navigator.clipboard.writeText(company.phone)
                  }}>
                  <BsTelephone className="item-icon" />
                  <span className="item-label">{company.phone}</span>
                </div>
              ) : null}

              {company?.email ? (
                <a
                  href={`mailto:${company.email}`}
                  className="d-flex align-items-center mt-3 pointer item-link">
                  <IoMailOutline className="item-icon" />
                  <span className="item-label">Email</span>
                </a>
              ) : null}

              {company?.website ? (
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href={
                    company.website.startsWith('http://') ||
                    company.website.startsWith('https://')
                      ? company.website
                      : `https://${company.website}`
                  }
                  className="d-flex align-items-center mt-3 pointer item-link">
                  <RxExternalLink className="item-icon" />
                  <span className="item-label">Web stranica</span>
                </a>
              ) : null}

              {company?.description ? (
                <div
                  onClick={() => setIsInfoModalVisible(true)}
                  className="d-flex align-items-center mt-3 pointer item-link">
                  <MdOutlineInfo className="item-icon" />
                  <span className="item-label">O nama</span>
                </div>
              ) : null}

              {isMyCompany ? null : (
                <div
                  onClick={onPrivateMessageClick}
                  className="d-flex align-items-center mt-3 pointer item-link">
                  <LuMessageCircleMore />
                  <span className="item-label">Pošalji poruku</span>
                </div>
              )}

              <hr className="hr divider" />

              {company?.num_of_employees ? (
                <div className="d-flex align-items-center mt-3">
                  <IoPeopleOutline className="item-icon" />
                  <span className="item-label">
                    {`Broj uposlenih: `}
                    <b>{company.num_of_employees}</b>
                  </span>
                </div>
              ) : null}

              {company?.type ? (
                <div className="d-flex align-items-center mt-3">
                  <MdWorkspacesOutline className="item-icon" />
                  <span className="item-label">
                    {`Vrsta: `}
                    <b>{company.type?.replace(',', ', ')}</b>
                  </span>
                </div>
              ) : null}
            </div>

            {/** Products */}
            <div className="products">
              {!selectedProduct && productLoader ? (
                <Loader customClasses="group-wrapper shadow align-items-center" />
              ) : null}

              {selectedProduct ? (
                <div className="group-wrapper shadow">
                  <ProductDetails
                    product={selectedProduct}
                    slides={slides}
                    openProductMessageModal={openProductMessageModal}
                    onSlideImageClick={onSlideImageClick}
                    isMyCompany={isMyCompany}
                  />
                </div>
              ) : null}

              <div className="group-wrapper shadow">
                <div className="d-flex align-items-center justify-content-between mb-4">
                  <h3 className="mb-0">Proizvodi i servisi</h3>
                  <Form
                    className="w-50 position-relative"
                    onSubmit={onSearchSubmit}>
                    <input
                      maxLength={60}
                      minLength={3}
                      type="text"
                      className="form-control rounded-pill ps-3 pe-4 truncate-line"
                      placeholder="Naziv proizvoda"
                      aria-label="Pretraga"
                      onChange={e => {
                        setSearchQuery(e.target.value)
                      }}
                      value={searchQuery}
                    />
                    {hasValue(searchQuery) ? (
                      <IoMdClose
                        onClick={clearSearch}
                        className="search-icon"
                      />
                    ) : (
                      <IoMdSearch className="search-icon" />
                    )}
                  </Form>
                </div>

                <Row>
                  {companyProducts?.map(product => {
                    return (
                      <Col
                        className="mb-4"
                        key={product.id}
                        xs={12}
                        sm={6}
                        md={4}
                        lg={3}>
                        <Product
                          product={product}
                          onCardClick={() => onCardClick(product.id)}
                          isCompanyVisible={false}
                        />
                      </Col>
                    )
                  })}
                </Row>

                {totalPages > 1 ? (
                  <PaginationList
                    currentPage={currentPage}
                    handlePageChange={handlePageChange}
                    totalPages={totalPages}
                  />
                ) : null}
              </div>
            </div>
          </div>
        ) : null}

        <CompanyInfoModal
          isModalVisible={isInfoModalVisible}
          closeModal={() => setIsInfoModalVisible(false)}
          companyName={company?.name}
          info={company?.description}
        />
      </Container>
      <Footer />

      {messagesModalInfo.visible ? (
        <MessageModal
          type={messagesModalInfo.type}
          userId={company?.user_id}
          product={selectedProduct}
          companyName={company?.name}
          closeModal={() =>
            setMessagesModalInfo({ visible: false, type: null })
          }
        />
      ) : null}

      {isLargeImagesSliderVisible ? (
        <div className="large-slider-modal">
          <div className="content">
            <Slider slides={slides} imgHeight={500} />
          </div>

          <IoMdClose onClick={() => setIsLargeImagesSliderVisible(false)} />
        </div>
      ) : null}
    </div>
  )
}
